// src/auth/silentReauth.js
//
// With cacheLocation: 'memory' (see index.js / auth0-client.js), all Auth0
// tokens live in memory and are lost on a full page reload. The SDK's only
// built-in recovery is a hidden-iframe checkSession, which fails when the
// browser blocks third-party cookies AND the tenant uses a default Auth0
// domain (custom domains are a paid tier).
//
// This module stores a *boolean* "a session existed" flag — never tokens.
// On reload, the app uses it to decide whether to attempt a full-page silent
// re-auth (authorize?prompt=none). Top-level navigations send the Auth0 SSO
// cookie even under third-party cookie blocking, so Auth0 bounces the user
// straight back into an authenticated session with no password and no click.

const FLAG_KEY = 'zbookkeeping.auth_session';

// Query params Auth0 appends to the redirect_uri on login callbacks.
// Presence of any of them means we're on a callback return (success or
// error) and must not fire another redirect.
const CALLBACK_PARAMS = ['code', 'state', 'error', 'error_description'];

export function hasSessionFlag() {
  try {
    return localStorage.getItem(FLAG_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setSessionFlag() {
  try {
    localStorage.setItem(FLAG_KEY, 'true');
  } catch {
    // storage unavailable (e.g. private browsing) — re-auth will simply be
    // skipped and the user lands on the Login screen.
  }
}

export function clearSessionFlag() {
  try {
    localStorage.removeItem(FLAG_KEY);
  } catch {
    // ignore
  }
}

export function hasStaleCallbackParams() {
  const query = new URLSearchParams(window.location.search);
  return CALLBACK_PARAMS.some((p) => query.has(p));
}

export function stripCallbackParams() {
  const url = new URL(window.location.href);
  let changed = false;
  for (const p of CALLBACK_PARAMS) {
    if (url.searchParams.has(p)) {
      url.searchParams.delete(p);
      changed = true;
    }
  }
  if (changed) {
    window.history.replaceState(
      {},
      document.title,
      url.pathname + url.search + url.hash
    );
  }
}
